import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { emailSequencesApi } from '@/api';
import { extractApiError, type ApiErrorInfo } from '@/api/errorStatus';
import type {
  EmailSequence,
  EmailSequenceStep,
  EmailSequenceLog,
  EmailSequencesState,
} from '@/types';
import type {
  CreateSequencePayload,
  UpdateSequencePayload,
  CreateStepPayload,
  UpdateStepPayload,
  LogsQueryParams,
} from '@/api/emailSequences';
import { pickStrings } from '@/i18n';
import { settingsStrings } from '@/i18n/dictionaries/settings';

/**
 * Разобранный отказ + ЗАПАСНАЯ ФРАЗА РАЗДЕЛА (WEB-1954).
 *
 * Прежний код отвергал `error instanceof Error ? error.message : фраза` —
 * то есть строкой, и `code`/`reasonCode` умирали в слайсе. Теперь наружу
 * уходит `ApiErrorInfo`, а запасная фраза раздела подставляется ровно там,
 * где своего текста у отказа нет вовсе (отказ не `Error` и не строка) —
 * прежнее поведение слово в слово, только код больше не теряется.
 */
function withFallbackMessage(error: unknown, fallback: string): ApiErrorInfo {
  const info = extractApiError(error);
  const hasOwnMessage = error instanceof Error || typeof error === 'string';
  return hasOwnMessage ? info : { ...info, message: fallback };
}

const initialState: EmailSequencesState = {
  sequences: [],
  isLoading: false,
  error: null,
  currentSequence: null,
  isLoadingDetail: false,
  // WEB-1329 п.4: отдельное от общего `error` поле — только для
  // fetchSequenceByIdThunk. Хранит status/message/code сервера, чтобы экран
  // цепочки мог отличить «сервер сказал, что такой цепочки нет» (404) от
  // «до сервера не достучались» (сеть/5xx/чужой проект).
  detailError: null,
  logs: [],
  isLoadingLogs: false,
  logsError: null,
  logsRequestKey: null,
};

// ---- List-level thunks ----

export const fetchSequencesThunk = createAsyncThunk(
  'emailSequences/fetchAll',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await emailSequencesApi.list(projectId);
      const payload = response.data as
        | EmailSequence[]
        | { data: EmailSequence[] };
      return Array.isArray(payload) ? payload : payload.data;
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqListLoadFailed),
      );
    }
  },
);

export const createSequenceThunk = createAsyncThunk(
  'emailSequences/create',
  async (
    { projectId, data }: { projectId: string; data: CreateSequencePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await emailSequencesApi.create(projectId, data);
      const payload = response.data as
        | EmailSequence
        | { data: EmailSequence };
      return 'data' in payload && (payload as { data: EmailSequence }).data
        ? (payload as { data: EmailSequence }).data
        : (payload as EmailSequence);
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqCreateFailed),
      );
    }
  },
);

export const updateSequenceThunk = createAsyncThunk(
  'emailSequences/update',
  async (
    {
      projectId,
      id,
      data,
    }: { projectId: string; id: string; data: UpdateSequencePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await emailSequencesApi.update(projectId, id, data);
      const payload = response.data as
        | EmailSequence
        | { data: EmailSequence };
      return 'data' in payload && (payload as { data: EmailSequence }).data
        ? (payload as { data: EmailSequence }).data
        : (payload as EmailSequence);
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqUpdateFailed),
      );
    }
  },
);

export const deleteSequenceThunk = createAsyncThunk(
  'emailSequences/delete',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue },
  ) => {
    try {
      await emailSequencesApi.delete(projectId, id);
      return id;
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqDeleteFailed),
      );
    }
  },
);

// ---- Detail-level thunks ----

export const fetchSequenceByIdThunk = createAsyncThunk(
  'emailSequences/fetchById',
  async (
    { projectId, id }: { projectId: string; id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await emailSequencesApi.get(projectId, id);
      const payload = response.data as EmailSequence | { data: EmailSequence };
      return 'data' in payload && (payload as { data: EmailSequence }).data
        ? (payload as { data: EmailSequence }).data
        : (payload as EmailSequence);
    } catch (error: unknown) {
      // WEB-1329 п.4: сохраняем status, чтобы страница отличала «сервер
      // сказал, что цепочки нет» (404) от «до сервера не достучались».
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const addStepThunk = createAsyncThunk(
  'emailSequences/addStep',
  async (
    {
      projectId,
      sequenceId,
      data,
    }: { projectId: string; sequenceId: string; data: CreateStepPayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await emailSequencesApi.addStep(
        projectId,
        sequenceId,
        data,
      );
      const payload = response.data as
        | EmailSequenceStep
        | { data: EmailSequenceStep };
      return 'data' in payload && (payload as { data: EmailSequenceStep }).data
        ? (payload as { data: EmailSequenceStep }).data
        : (payload as EmailSequenceStep);
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqStepAddFailed),
      );
    }
  },
);

export const updateStepThunk = createAsyncThunk(
  'emailSequences/updateStep',
  async (
    {
      projectId,
      sequenceId,
      stepId,
      data,
    }: {
      projectId: string;
      sequenceId: string;
      stepId: string;
      data: UpdateStepPayload;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await emailSequencesApi.updateStep(
        projectId,
        sequenceId,
        stepId,
        data,
      );
      const payload = response.data as
        | EmailSequenceStep
        | { data: EmailSequenceStep };
      return 'data' in payload && (payload as { data: EmailSequenceStep }).data
        ? (payload as { data: EmailSequenceStep }).data
        : (payload as EmailSequenceStep);
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqStepSaveFailed),
      );
    }
  },
);

export const deleteStepThunk = createAsyncThunk(
  'emailSequences/deleteStep',
  async (
    {
      projectId,
      sequenceId,
      stepId,
    }: { projectId: string; sequenceId: string; stepId: string },
    { rejectWithValue },
  ) => {
    try {
      await emailSequencesApi.deleteStep(projectId, sequenceId, stepId);
      return stepId;
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqStepDeleteFailed),
      );
    }
  },
);

/** Ключ запроса журнала: цепочка + отбор — по нему отбрасываются опоздавшие ответы (ревизия пачки 40, Б8). */
export function logsRequestKey(arg: {
  projectId: string;
  sequenceId: string;
  params?: LogsQueryParams;
}): string {
  return JSON.stringify([arg.projectId, arg.sequenceId, arg.params ?? null]);
}

export const fetchLogsThunk = createAsyncThunk(
  'emailSequences/fetchLogs',
  async (
    {
      projectId,
      sequenceId,
      params,
    }: { projectId: string; sequenceId: string; params?: LogsQueryParams },
    { rejectWithValue },
  ) => {
    try {
      const response = await emailSequencesApi.getLogs(
        projectId,
        sequenceId,
        params,
      );
      const payload = response.data as
        | EmailSequenceLog[]
        | { data: EmailSequenceLog[] };
      return Array.isArray(payload) ? payload : payload.data;
    } catch (error: unknown) {
      // WEB-1954: отказ уходит РАЗОБРАННЫМ — со `status`, `code` и
      // `reasonCode`. Строка убивала код, и экран по построению не мог
      // спросить реестр кодов (правило WEB-1874).
      return rejectWithValue(
        withFallbackMessage(error, pickStrings(settingsStrings).seqLogsLoadFailed),
      );
    }
  },
);

// ---- Slice ----

const emailSequencesSlice = createSlice({
  name: 'emailSequences',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchAll
    builder
      .addCase(fetchSequencesThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSequencesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sequences = action.payload;
      })
      .addCase(fetchSequencesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as ApiErrorInfo;
      });

    // create
    builder
      .addCase(createSequenceThunk.fulfilled, (state, action) => {
        state.sequences = [action.payload, ...state.sequences];
      })
      .addCase(createSequenceThunk.rejected, (state, action) => {
        state.error = action.payload as ApiErrorInfo;
      });

    // update
    builder.addCase(updateSequenceThunk.fulfilled, (state, action) => {
      state.sequences = state.sequences.map((s) =>
        s.id === action.payload.id ? action.payload : s,
      );
      // Also patch currentSequence if it's the same one
      if (state.currentSequence?.id === action.payload.id) {
        state.currentSequence = {
          ...action.payload,
          steps: state.currentSequence.steps,
        };
      }
    });

    // delete
    builder.addCase(deleteSequenceThunk.fulfilled, (state, action) => {
      state.sequences = state.sequences.filter((s) => s.id !== action.payload);
    });

    // fetchById
    builder
      .addCase(fetchSequenceByIdThunk.pending, (state) => {
        state.isLoadingDetail = true;
        state.error = null;
        state.detailError = null;
      })
      .addCase(fetchSequenceByIdThunk.fulfilled, (state, action) => {
        state.isLoadingDetail = false;
        state.currentSequence = action.payload;
        state.detailError = null;
      })
      .addCase(fetchSequenceByIdThunk.rejected, (state, action) => {
        state.isLoadingDetail = false;
        // WEB-1329 п.4: НЕ оставляем цепочку прошлого успешного запроса на
        // экране — иначе она выглядит как настоящая (сорванная загрузка,
        // смена проекта/цепочки, удаление в соседней вкладке).
        state.currentSequence = null;
        state.detailError = action.payload as ApiErrorInfo;
      });

    // addStep
    builder
      .addCase(addStepThunk.fulfilled, (state, action) => {
        if (state.currentSequence) {
          state.currentSequence = {
            ...state.currentSequence,
            steps: [...(state.currentSequence.steps ?? []), action.payload],
          };
        }
      })
      // WEB-1276 п.5: не было обработчика отказа вовсе — состояние ничего
      // не знало о нём.
      .addCase(addStepThunk.rejected, (state, action) => {
        state.error = action.payload as ApiErrorInfo;
      });

    // updateStep
    builder
      .addCase(updateStepThunk.fulfilled, (state, action) => {
        if (state.currentSequence?.steps) {
          state.currentSequence = {
            ...state.currentSequence,
            steps: state.currentSequence.steps.map((s) =>
              s.id === action.payload.id ? action.payload : s,
            ),
          };
        }
      })
      .addCase(updateStepThunk.rejected, (state, action) => {
        state.error = action.payload as ApiErrorInfo;
      });

    // deleteStep
    builder
      .addCase(deleteStepThunk.fulfilled, (state, action) => {
        if (state.currentSequence?.steps) {
          state.currentSequence = {
            ...state.currentSequence,
            steps: state.currentSequence.steps.filter(
              (s) => s.id !== action.payload,
            ),
          };
        }
      })
      .addCase(deleteStepThunk.rejected, (state, action) => {
        state.error = action.payload as ApiErrorInfo;
      });

    // fetchLogs
    builder
      /**
       * WEB-1368 п.2.2: у журнала отправок появилось ТРЕТЬЕ состояние —
       * «ошибка». Раньше отказ снимал только признак загрузки: экран рисовал
       * «Логов пока нет», а если до этого был открыт журнал ДРУГОЙ цепочки —
       * её строки оставались на экране как свои.
       */
      .addCase(fetchLogsThunk.pending, (state, action) => {
        state.isLoadingLogs = true;
        state.logsError = null;
        state.logsRequestKey = logsRequestKey(action.meta.arg);
      })
      .addCase(fetchLogsThunk.fulfilled, (state, action) => {
        // Ревизия пачки 40 (Б8): фильтры применяются сами (п.2.3), значит два
        // быстрых клика дают два запроса — ответ прежнего отбора, пришедший
        // позже, не имеет права лечь поверх свежего (тот же класс, что счётчик
        // поколений в JournalPanel).
        // Ключ пуст — запроса в полёте нет (прямой диспатч без pending): принимаем.
        if (
          state.logsRequestKey !== null &&
          logsRequestKey(action.meta.arg) !== state.logsRequestKey
        )
          return;
        state.isLoadingLogs = false;
        state.logs = action.payload;
        state.logsError = null;
      })
      .addCase(fetchLogsThunk.rejected, (state, action) => {
        // Ключ пуст — запроса в полёте нет (прямой диспатч без pending): принимаем.
        if (
          state.logsRequestKey !== null &&
          logsRequestKey(action.meta.arg) !== state.logsRequestKey
        )
          return;
        state.isLoadingLogs = false;
        state.logs = [];
        state.logsError = action.payload as ApiErrorInfo;
      });
  },
});

export default emailSequencesSlice.reducer;
